import { memo, useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { LongFormattedDateTime } from "common/core/format/date";
import Button from "common/core/button";
import { useQuery } from "util/graphql";

import Styles from "./index.module.scss";
import { MeetingDigicertOTPModal } from "./digicert_otp";
import NotaryMeetingsPendingDigitalSignatureQuery, {
  type NotaryMeetingsPendingDigitalSignature,
  type NotaryMeetingsPendingDigitalSignature_viewer_meetings_edges_node_meetingParticipants as MeetingParticipant,
} from "./index_query.graphql";

const POLL_INTERVAL = 5 * 60_000; // 5 min

function getSignerName(meetingParticipants: MeetingParticipant[]): ReactNode {
  return (
    meetingParticipants.find((part) => part.__typename === "SignerParticipant")?.fullName || (
      <FormattedMessage id="59396757-257f-4554-94f4-eb4551daffbb" defaultMessage="Unknown" />
    )
  );
}

function PendingCertificationMeetingListing() {
  const { data } = useQuery(NotaryMeetingsPendingDigitalSignatureQuery, {
    pollInterval: POLL_INTERVAL,
  });
  const [openOTPMeetingId, setOpenOTPMeetingId] = useState<string | null>(null);

  const meetingEdges = data?.viewer.meetings.edges;
  if (!meetingEdges?.length) {
    return null;
  }

  return (
    <>
      <h3 className={Styles.header}>
        <FormattedMessage
          id="561d0fa7-97f5-4d6b-82f5-73de581fa3ae"
          defaultMessage="Needs attention"
        />
      </h3>
      <ul className={Styles.list}>
        {meetingEdges.map(({ node: meeting }) => (
          <li key={meeting.id}>
            <LongFormattedDateTime value={meeting.timeFrame!.endedAt} />

            <FormattedMessage
              id="6649e381-56a5-4aa6-b5c4-6ada023dc49c"
              defaultMessage="With {name}"
              values={{ name: getSignerName(meeting.meetingParticipants) }}
              tagName="strong"
            />

            <div className={Styles.tag}>
              <FormattedMessage
                id="104c96cf-794d-4488-a9f7-f8ff5648a548"
                defaultMessage="Ready to tamper seal"
              />
            </div>

            <Button
              buttonColor="action"
              variant="secondary"
              onClick={() => setOpenOTPMeetingId(meeting.id)}
            >
              <FormattedMessage
                id="5dbd3604-5ef1-40c6-acd2-a672a9b9dbfe"
                defaultMessage="Complete notarization"
              />
            </Button>
          </li>
        ))}
      </ul>
      {openOTPMeetingId && (
        <MeetingDigicertOTPModal
          meetingId={openOTPMeetingId}
          onSuccess={() => setOpenOTPMeetingId(null)}
          onCacheUpdate={(cacheProxy) => {
            const oldData = cacheProxy.readQuery<NotaryMeetingsPendingDigitalSignature>({
              query: NotaryMeetingsPendingDigitalSignatureQuery,
            })!;
            cacheProxy.writeQuery<NotaryMeetingsPendingDigitalSignature>({
              query: NotaryMeetingsPendingDigitalSignatureQuery,
              data: {
                ...oldData,
                viewer: {
                  ...oldData.viewer,
                  meetings: {
                    ...oldData.viewer.meetings,
                    edges: oldData.viewer.meetings.edges.filter(
                      ({ node }) => node.id !== openOTPMeetingId,
                    ),
                  },
                },
              },
            });
          }}
        />
      )}
    </>
  );
}

export default memo(PendingCertificationMeetingListing);
